import React, { useEffect, useState } from 'react'
import { DialogTitle, DialogContent, Dialog, Button } from '@mui/material'
import { Button as DialogButton } from '@/ui'
import styles from '../ContractCard.module.scss'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import { request } from '@/shared/api/request'
import { useDigitalSign } from '@/shared'
import { LoadingBlocker } from '@/components/Loader'
import { contractsActions, contractsSelectors, contractsThunks } from '@/entity/contracts'
import { useDispatch, useSelector } from 'react-redux'
import Timer from '@features/contracts/actions/view/components/Timer/Timer'
import { message } from 'antd'

export const SignModal = ({ onClose, contract, updateContract }) => {
	const {
		t,
		i18n: { language },
	} = useTranslation()
	const dispatch = useDispatch()
	const [templatesLoading, setTemplatesLoading] = useState(false)
	const { successPayload, onClearPayload, onSignMultiple } = useDigitalSign()
	const [statusSign, setStatusSign] = useState(null)
	const [qr, setQr] = useState(false)
	const [qrTimer, setQrTimer] = useState(null)
	const isSuccess = useSelector(contractsSelectors.isSuccessEtd)
	const [qrStatusInterval, setQrStatusInterval] = useState(null)
	const status = useSelector(contractsSelectors.statusEtd)
	const isLoading = useSelector(contractsSelectors.isLoadingEtd)
	const errorEtd = useSelector(contractsSelectors.errorEtd)
	const image = useSelector(contractsSelectors.imageEtd)
	const imageKey = useSelector(contractsSelectors.imageKey)

	useEffect(() => {
		return () => {
			//удаляется, прерываем интервала
			if (!qr) clearInterval(qrStatusInterval)
		}
	}, [qrStatusInterval, qr])
	const resetDataQr = () => {
		setQr(false)
		dispatch(contractsActions.resetEtdQR())
	}

	useEffect(() => {
		if (status && status?.code !== 'no_data') {
			setStatusSign(language === 'rus' ? status.name_ru : status.name_kz)
		}
	}, [status])

	useEffect(() => {
		let intervalId
		if (qr && imageKey) {
			intervalId = setInterval(() => {
				dispatch(contractsThunks.getStatusQrSign(imageKey))
			}, 10000)
			setQrStatusInterval(intervalId)
		}
		return () => {
			if (intervalId) {
				clearInterval(intervalId)
			}
		}
	}, [qr, imageKey])

	useEffect(() => {
		if (isSuccess) {
			updateContract(false)
			resetDataQr()
			onClose()
		}
	}, [isSuccess])

	useEffect(() => {
		if (successPayload) sendSignedEtd(successPayload)
	}, [successPayload])

	const handleSign = async () => {
		try {
			setTemplatesLoading(true)
			const res = await request({
				url: '/contract/econtract/employer/xml',
				method: 'GET',
				params: {
					externalId: contract.externalId,
				},
			})
			onSignMultiple(res)
		} catch (error) {
			const err = error?.response?.data?.message || t('unknown_error')
			message.error(err ? err : t('unknown_error'), 5)
		} finally {
			setTemplatesLoading(false)
		}
	}
	const sendSignedEtd = async (xml) => {
		try {
			setTemplatesLoading(true)
			const result = await request({
				url: '/contract/econtract/employer/saveSign',
				method: 'POST',
				data: xml,
				headers: {
					'content-type': 'application/json',
				},
			})
			updateContract(false)
			onClearPayload()
			message.success(t('success_alert'))
			onClose()
		} catch (error) {
			const err = error?.response?.data?.message || t('unknown_error')
			alert(err ? err : t('unknown_error'))
		} finally {
			setTemplatesLoading(false)
		}
	}

	const handleSignQr = async (qrTimer) => {
		try {
			setTemplatesLoading(true)
			setStatusSign(null)
			dispatch(contractsThunks.getImageQr(contract.externalId, 'ec_employer'))
			setQr(true)
			// Если таймер уже существует, отменяем его
			if (qrTimer) {
				clearTimeout(qrTimer)
			}
			// Устанавливаем новый таймер для сброса состояния qr через 1 минуту (60000 миллисекунд)
			const newQrTimer = setTimeout(() => {
				setQr(false)
				setStatusSign(t('time_out'))
			}, 60000) // 60000 миллисекунд = 1 минута
			setQrTimer(newQrTimer) // Сохраняем идентификатор нового таймера в состоянии
		} catch (error) {
			const err = error?.response?.data?.message || t('unknown_error')
			message.error(err ? err : t('unknown_error'), 5)
		} finally {
			setTemplatesLoading(false)
		}
	}

	return (
		<Dialog classes={{ paper: styles.sign }} onClose={onClose} open={true}>
			{templatesLoading && <LoadingBlocker />}
			<DialogTitle classes={{ root: styles.sign__title }}>
				{qr ? (
					<>
						{t('text_qr_sign_employee1_biz')} <br />
						{t('text_qr_sign_employee2')} <br />
						{t('text_qr_sign_employee3')}
					</>
				) : (
					t('text_edt_sign')
				)}
			</DialogTitle>
			<DialogContent sx={{ paddingBottom: '40px' }}>
				<div className={styles.dialog__buttons__wrapper}>
					{!qr ? (
						<div className={styles.flex_button}>
							<div className={styles.margin_button}>
								<DialogButton type="button" onClick={handleSign}>
									{t('sign_button_ecp')}
								</DialogButton>
							</div>
							<div className={styles.margin_button}>
								<DialogButton type="button" onClick={handleSignQr.bind(null, qrTimer)}>
									{t('sign_button_qr')}
								</DialogButton>
							</div>
						</div>
					) : null}
					{qr && contract?.externalId && !isLoading ? (
						<>
							{errorEtd ? (
								<div className={styles.error__qr}>
									{errorEtd === 'was_signed' ? t('error_was_signed') : t('error_qr')}
								</div>
							) : (
								<>
									<Timer qr={qr}></Timer>
									<img src={image} alt={'QR Code'} />
								</>
							)}
						</>
					) : (
						<div className={styles.error__qr}>{statusSign}</div>
					)}
					<Button
						type="button"
						variant="text"
						classes={{ root: cn(styles.button, styles.reset__button) }}
						onClick={onClose}
					>
						{t('cancel')}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
